import { z } from "zod";

/**
 * The user as the APP is allowed to see it. The service has more columns; what is missing
 * here is missing on purpose (role-based visibility is decided by each API domain's views.ts —
 * never by filtering on the client).
 */
export const sessionUserSchema = z.object({
  id: z.string(),
  email: z.email(),
  name: z.string().nullable(),
  imageUrl: z.string().nullable(),
  emailVerified: z.boolean(),
  createdAt: z.iso.datetime(),
});

export type SessionUserDTO = z.infer<typeof sessionUserSchema>;

export const sessionSchema = z.object({
  user: sessionUserSchema,
  expiresAt: z.iso.datetime(),
});

export type SessionDTO = z.infer<typeof sessionSchema>;

/**
 * ONE NUMBER, BOTH SIDES.
 *
 * The service enforces it (`minPasswordLength` in api/src/shared/auth.ts) and the form has
 * to be able to say it out loud BEFORE the round trip. Written twice it drifts, and the
 * drift is silent in the direction that matters: the app promises a rule the service does
 * not have, so the user types a password the form accepted and the service refused.
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * WHY AN IDENTITY CALL WAS REFUSED — precise enough to word beside the field that caused it.
 *
 * `VALIDATION_FAILED` on its own is exactly as useful as nothing when the one fixable
 * detail — the password is three characters short — is the thing it does not say. These
 * are the auth library's own codes, restated here because the app switches on them and the
 * translation layer turns them into a sentence; anything not in this list is deliberately
 * not shown to a user verbatim.
 */
export const AUTH_REASONS = {
  PASSWORD_TOO_SHORT: "PASSWORD_TOO_SHORT",
  PASSWORD_TOO_LONG: "PASSWORD_TOO_LONG",
  INVALID_EMAIL: "INVALID_EMAIL",
  INVALID_EMAIL_OR_PASSWORD: "INVALID_EMAIL_OR_PASSWORD",
  INVALID_PASSWORD: "INVALID_PASSWORD",
  USER_ALREADY_EXISTS: "USER_ALREADY_EXISTS",
  USER_NOT_FOUND: "USER_NOT_FOUND",
  EMAIL_NOT_VERIFIED: "EMAIL_NOT_VERIFIED",
} as const;

export type AuthReason = (typeof AUTH_REASONS)[keyof typeof AUTH_REASONS];

/**
 * Which field a refusal belongs beside. A reason with no field here is a form-level
 * failure and is worded above the button instead.
 */
export const AUTH_REASON_FIELD: Record<AuthReason, "email" | "password"> = {
  PASSWORD_TOO_SHORT: "password",
  PASSWORD_TOO_LONG: "password",
  INVALID_PASSWORD: "password",
  INVALID_EMAIL_OR_PASSWORD: "password",
  INVALID_EMAIL: "email",
  USER_ALREADY_EXISTS: "email",
  USER_NOT_FOUND: "email",
  EMAIL_NOT_VERIFIED: "email",
};

/** The library reports "already exists" under two spellings; both mean the same thing here. */
export function toAuthReason(code: string | undefined): AuthReason | null {
  if (!code) return null;
  const normalised = code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL" ? "USER_ALREADY_EXISTS" : code;
  return normalised in AUTH_REASON_FIELD ? (normalised as AuthReason) : null;
}

/** Sign-in and sign-up are the auth library's own routes; these are the payload shapes. */
export const credentialsSchema = z.object({
  email: z.email(),
  password: z.string().min(MIN_PASSWORD_LENGTH, `at least ${MIN_PASSWORD_LENGTH} characters`),
});

export const signUpSchema = credentialsSchema.extend({
  name: z.string().min(1).max(80),
});

export type Credentials = z.infer<typeof credentialsSchema>;
export type SignUpPayload = z.infer<typeof signUpSchema>;
